import { useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { State } from "store/reducers";
import ls from "Localization";
import _ from "lodash";
import { Helmet } from "react-helmet-async";

import actions from "store/actions";

import { SubscriptionState } from "store/reducers/subscription";
import ISubscription, { SubscriptionStatus } from "Types/Subscription";
import IPlan from "Types/Plan";
import IPaymentMethod from "Types/PaymentMethod";
import dayjs from "dayjs";
import Report from "Components/Report";
import { dateSort } from "Components/Report/helpers";
import { toStringCurrency } from "helpers/string";

// import ImportModal from "./Import";
import ReactTable from "react-table";
import { Fab, IconButton } from "@mui/material";
import { MdAdd, MdAttachMoney } from "react-icons/md";

import useStyles from "./styles";
import { useNavigate } from "react-router-dom";

const ls2: any = ls;

const columns = [
	{
		Header: ls.creationDate,
		id: "createdAt",
		accessor: (c: ISubscription) =>
			dayjs(c.createdAt).format(ls.dateFormatWithoutSeconds),
		show: false,
		sortMethod: dateSort,
		width: 150
	},
	{
		Header: ls.user,
		id: "user",
		show: true,
		accessor: (c: ISubscription) =>
			`${c.user?.name ?? ""} ${c.user?.lastname ?? ""}`
	},
	{
		Header: ls.plan,
		id: "plan",
		show: true,
		accessor: (c: ISubscription) => (c.plan as IPlan)?.name,
		width: 150
	},
	{
		Header: ls.paymentMethod,
		id: "paymentMethod",
		show: true,
		accessor: (c: ISubscription) => (c.paymentMethod as IPaymentMethod)?.name,
		width: 180
	},
	{
		Header: ls.value,
		id: "value",
		show: true,
		filterable: false,
		accessor: (c: ISubscription) => toStringCurrency(+c.value / 100),
		width: 150
	},
	{
		Header: ls.discount,
		id: "discount",
		show: true,
		filterable: false,
		accessor: (c: ISubscription) => toStringCurrency(+c.discount || 0 / 100),
		width: 150
	},
	{
		Header: ls.installment,
		id: "installment",
		show: false,
		filterable: false,
		accessor: (c: ISubscription) => c.installment,
		width: 150
	},
	{
		Header: ls.description,
		accessor: "description",
		show: false
	},
	{
		Header: ls.status,
		id: "status",
		show: true,
		accessor: (c: ISubscription) => ls2[SubscriptionStatus[c.status]],
		width: 140
	}
	// {
	// 	Header: ls.dueDate,
	// 	id: "dueDate",
	// 	accessor: (c: ISubscription) => c.dueDate,
	// 	show: true,
	// 	sortMethod: dateSort,
	// },
];

let getSubscriptionsDebounded: (() => any) & _.Cancelable;

interface TableState {
	page: number;
	pageSize: number;
	sorted: any[];
	filtered: any[];
	toExport: boolean;
	callback: any;
}

function Subscriptions() {
	const navigate = useNavigate();
	const classes = useStyles({});
	const dispatch = useDispatch<any>();
	const table = useRef<ReactTable<any>>(null);

	const state = useSelector<State, SubscriptionState>(s => s.subscription);

	const handleFetchData = (tableState: TableState, instance: any) => {
		if (!tableState) return;
		const { page, pageSize, sorted, filtered, toExport, callback } = tableState;

		if (getSubscriptionsDebounded) {
			getSubscriptionsDebounded.cancel();
		}

		if (!tableState.sorted) {
			tableState.sorted = [];
		}

		const creationDate = tableState.filtered.find(
			(c: any) => c.id === "creationDate"
		);

		if (creationDate?.value) {
			tableState.filtered = tableState.filtered.filter(
				(c: any) => c.id !== "creationDate"
			);

			if (creationDate?.value.startDate) {
				tableState.filtered.push({
					id: "startDate",
					value: creationDate.value.startDate
				});
			}

			if (creationDate?.value.endDate) {
				tableState.filtered.push({
					id: "endDate",
					value: creationDate.value.endDate
				});
			}
		}

		getSubscriptionsDebounded = _.debounce(
			() =>
				dispatch(
					actions.subscription.getSubscriptions(
						page * pageSize,
						pageSize,
						tableState.filtered.reduce(
							(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
							""
						),
						tableState.sorted[0]?.id,
						tableState.sorted[0]?.desc,
						toExport,
						callback
					)
				),
			500
		);

		getSubscriptionsDebounded();
	};

	// const load = () => handleFetchData(table.current?.state, null);

	return (
		<div>
			<Helmet>
				<title>
					{ls.subscriptions} | {ls.appName}
				</title>
			</Helmet>
			<Report
				titleText=""
				manual
				tableRef={table}
				headerRightComponent={
					<div>
						<IconButton
							onClick={() => navigate("/Dashboard/Subscriptions/Pay")}
						>
							<MdAttachMoney />
						</IconButton>
					</div>
				}
				title={ls.subscriptions}
				data={state.items}
				pages={state.pages}
				loading={state.loading.getAll}
				columns={columns}
				onFetchData={handleFetchData}
				filterable
				visibleColumns={columns.filter(c => c.show).map(c => c.Header)}
				defaultSorted={[
					{
						id: "createdAt",
						desc: true
					}
				]}
				getTdProps={() => {
					return {
						style: {
							display: "flex",
							alignItems: "center"
						}
					};
				}}
				onRowClicked={data => navigate(`/Dashboard/Subscriptions/${data._id}`)}
			/>

			<Fab
				color="primary"
				aria-label="Add"
				style={{ position: 'fixed', bottom: '16px', right: '16px',  zIndex: 1000}}
				onClick={() => navigate("/Dashboard/Subscriptions/Create")}
			>
				<MdAdd size={24} />
			</Fab>
		</div>
	);
}

export default Subscriptions;
